import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch, getToken } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const CATEGORIES = ["hardware", "software", "configuration", "process", "human", "external",
                    "unknown"];

export default function Problems() {
  const [detection, setDetection] = useState(null);
  const [impact, setImpact] = useState(null);
  const [problems, setProblems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ hypothesis: "", evidence: "", cause: "",
                                      category: "unknown", workaround: "",
                                      actionTitle: "", actionOwner: "", actionDue: "" });
  const navigate = useNavigate();

  const refresh = useCallback(() => {
    apiFetch("/problems/candidates").then(async (r) => {
      if (r.status === 403) { setError("You do not have problem management access (problem.write)."); return; }
      setDetection(await r.json());
    });
    apiFetch("/problems/impact").then((r) => (r.ok ? r.json() : null)).then(setImpact);
    apiFetch("/problems").then((r) => (r.ok ? r.json() : [])).then(setProblems);
  }, []);

  useEffect(() => {
    if (!getToken()) { navigate("/login"); return; }
    refresh();
  }, [navigate, refresh]);

  const open = (id) =>
    apiFetch(`/problems/${id}`).then((r) => (r.ok ? r.json() : null)).then(setSelected);

  const post = (path, body, label) =>
    apiFetch(path, { method: "POST", body: JSON.stringify(body) }).then(async (r) => {
      const data = await r.json();
      if (!r.ok) { setStatus(`${label} failed — ${JSON.stringify(data.detail)}`); return; }
      setStatus(`${label} recorded`);
      if (data.id) setSelected(data);
      else if (data.problem) setSelected(data.problem);
      refresh();
    });

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold" data-testid="problems-title">Problem Management</h1>
          <p className="text-sm text-muted-foreground">
            Repeat work becomes an investigation, an owned action, and prevented demand.
          </p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-6 space-y-8">
        {error && <p className="text-sm text-destructive" data-testid="problems-error">{error}</p>}
        {status && <p className="text-sm text-amber-800" data-testid="problems-status">{status}</p>}

        {impact && (
          <section className="space-y-2" data-testid="problem-impact">
            <h2 className="text-lg font-medium">Impact</h2>
            <p className="text-sm text-muted-foreground" data-testid="problem-impact-method">
              {impact.definition} Method: {impact.method}
            </p>
            <div className="grid gap-3 md:grid-cols-4 text-sm">
              {[["Recurrence clusters", impact.recurrence_clusters, "clusters"],
                ["Repeat work under investigation", impact.repeat_incidents_under_investigation, "investigated"],
                ["Repeat work not investigated", impact.repeat_incidents_not_yet_investigated, "uninvestigated"],
                ["Root cause completeness", impact.root_cause_completeness_pct === null
                  ? "—" : `${impact.root_cause_completeness_pct}%`, "rootcause"],
                ["Known errors published", impact.known_error_articles_published, "knownerrors"],
                ["Open actions", impact.actions_open, "actionsopen"],
                ["Completed actions", impact.actions_completed, "actionsdone"],
                ["Major incidents handed off", `${impact.major_incidents_handed_off}/${impact.major_incidents}`, "handoff"],
              ].map(([label, value, id]) => (
                <div key={id} className="border rounded-md px-3 py-2" data-testid={`impact-${id}`}>
                  <p className="text-muted-foreground text-xs">{label}</p>
                  <p className="text-2xl font-semibold">{value}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {detection && (
          <section className="space-y-3" data-testid="recurrence-candidates">
            <h2 className="text-lg font-medium">Recurrence candidates</h2>
            <p className="text-sm text-muted-foreground" data-testid="recurrence-method">
              {detection.method}. {detection.records_scanned} closed records scanned.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border rounded-md">
                <caption className="sr-only">Clusters of repeat closed work</caption>
                <thead>
                  <tr className="text-left border-b">
                    <th scope="col" className="px-3 py-2">Symptom</th>
                    <th scope="col" className="px-3 py-2">Catalog item</th>
                    <th scope="col" className="px-3 py-2">Site</th>
                    <th scope="col" className="px-3 py-2">Occurrences</th>
                    <th scope="col" className="px-3 py-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {detection.candidates.map((c) => (
                    <tr key={c.signature} className="border-b last:border-0"
                        data-testid={`candidate-row-${c.signature}`}>
                      <th scope="row" className="px-3 py-2 font-medium text-left">{c.symptom}</th>
                      <td className="px-3 py-2">{c.service_key || "—"}</td>
                      <td className="px-3 py-2">{c.location_key || "multiple"}</td>
                      <td className="px-3 py-2">{c.occurrences}</td>
                      <td className="px-3 py-2">
                        {c.existing_problem_id ? (
                          <Button size="sm" variant="ghost" onClick={() => open(c.existing_problem_id)}
                                  data-testid={`candidate-open-${c.signature}`}>
                            Open investigation
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline"
                                  data-testid={`candidate-promote-${c.signature}`}
                                  onClick={() => post("/problems/candidates/promote",
                                                       { signature: c.signature }, "Investigation opened")}>
                            Investigate
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {detection.candidates.length === 0 && (
                    <tr><td colSpan="5" className="px-3 py-2 text-muted-foreground"
                            data-testid="candidates-empty">
                      No cluster has reached {detection.policy.min_occurrences} occurrences in the
                      last {detection.policy.window_days} days.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        <section className="space-y-2" data-testid="problem-list">
          <h2 className="text-lg font-medium">Problems</h2>
          <ul className="space-y-1">
            {problems.slice(0, 25).map((p) => (
              <li key={p.id}>
                <button className="w-full text-left text-sm border rounded px-3 py-2 hover:border-foreground/40"
                        data-testid={`problem-row-${p.number}`} onClick={() => open(p.id)}>
                  <span className="font-mono">{p.number}</span> · {p.title} ·{" "}
                  <span className="text-muted-foreground">{p.state}</span>
                </button>
              </li>
            ))}
            {problems.length === 0 && (
              <li className="text-sm text-muted-foreground" data-testid="problem-list-empty">
                No problems recorded yet.
              </li>
            )}
          </ul>
        </section>

        {selected && (
          <section className="space-y-4 border-t pt-6" data-testid="problem-detail">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium" data-testid="problem-detail-title">
                {selected.number} — {selected.title}
              </h2>
              <Button size="sm" variant="ghost" data-testid="problem-detail-close"
                      onClick={() => setSelected(null)}>Close</Button>
            </div>
            <p className="text-sm" data-testid="problem-detail-state">
              State: {selected.state} · priority {selected.priority}
              {selected.closure_blockers.length > 0 && (
                <span className="text-amber-800" data-testid="problem-closure-blockers">
                  {" "}· cannot close: {selected.closure_blockers.join("; ")}
                </span>
              )}
            </p>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="border rounded-md p-4 space-y-2">
                <h3 className="text-sm font-semibold">Hypotheses and evidence</h3>
                <ul className="text-sm space-y-1" data-testid="problem-hypotheses">
                  {(selected.hypotheses || []).map((h) => (
                    <li key={h.id} data-testid={`hypothesis-${h.id}`}>
                      {h.statement} <span className="text-muted-foreground">({h.status})</span>
                    </li>
                  ))}
                </ul>
                <ul className="text-sm space-y-1" data-testid="problem-evidence">
                  {(selected.evidence || []).map((e) => (
                    <li key={e.id} className="text-muted-foreground">
                      {e.supports ? "supports" : "refutes"}: {e.statement}
                    </li>
                  ))}
                </ul>
                <Input placeholder="New hypothesis" value={form.hypothesis}
                       data-testid="hypothesis-input"
                       onChange={(e) => setForm({ ...form, hypothesis: e.target.value })} />
                <Button size="sm" variant="outline" data-testid="hypothesis-add"
                        onClick={() => post(`/problems/${selected.id}/hypotheses`,
                                             { statement: form.hypothesis }, "Hypothesis")}>
                  Add hypothesis
                </Button>
                <Input placeholder="Evidence" value={form.evidence} data-testid="evidence-input"
                       onChange={(e) => setForm({ ...form, evidence: e.target.value })} />
                <Button size="sm" variant="outline" data-testid="evidence-add"
                        onClick={() => post(`/problems/${selected.id}/evidence`,
                                             { statement: form.evidence }, "Evidence")}>
                  Add evidence
                </Button>
              </div>

              <div className="border rounded-md p-4 space-y-2">
                <h3 className="text-sm font-semibold">Root cause</h3>
                {selected.root_cause_record ? (
                  <p className="text-sm" data-testid="problem-root-cause">
                    {selected.root_cause_record.statement} ({selected.root_cause_record.category})
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">Not recorded yet.</p>
                )}
                <Input placeholder="Root cause statement" value={form.cause}
                       data-testid="root-cause-input"
                       onChange={(e) => setForm({ ...form, cause: e.target.value })} />
                <select className="border rounded-md px-2 py-1 text-sm bg-background w-full"
                        aria-label="Root cause category" data-testid="root-cause-category"
                        value={form.category}
                        onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <Input placeholder="Workaround" value={form.workaround}
                       data-testid="workaround-input"
                       onChange={(e) => setForm({ ...form, workaround: e.target.value })} />
                <Button size="sm" variant="outline" data-testid="root-cause-save"
                        onClick={() => post(`/problems/${selected.id}/root-cause`,
                                             { statement: form.cause, category: form.category,
                                               workaround: form.workaround }, "Root cause")}>
                  Record root cause
                </Button>
                <Button size="sm" variant="outline" data-testid="publish-known-error"
                        onClick={() => post(`/problems/${selected.id}/publish-known-error`, {},
                                             "Known error published")}>
                  Publish known error
                </Button>
              </div>
            </div>

            <div className="border rounded-md p-4 space-y-2">
              <h3 className="text-sm font-semibold">Corrective and preventive actions</h3>
              <ul className="text-sm space-y-1" data-testid="problem-actions">
                {(selected.actions || []).map((a) => (
                  <li key={a.id} className="flex items-center gap-2"
                      data-testid={`action-${a.id}`}>
                    <span>{a.kind}: {a.title} · {a.owner_email} · due {a.due_date} · {a.status}</span>
                    {a.status === "open" && (
                      <Button size="sm" variant="ghost" data-testid={`action-complete-${a.id}`}
                              onClick={() => post(`/problems/${selected.id}/actions/${a.id}/decide`,
                                                   { status: "completed" }, "Action completed")}>
                        Complete
                      </Button>
                    )}
                  </li>
                ))}
                {(selected.actions || []).length === 0 && (
                  <li className="text-muted-foreground" data-testid="problem-actions-empty">
                    No actions yet — a problem cannot close without follow-through.
                  </li>
                )}
              </ul>
              <div className="grid gap-2 md:grid-cols-3">
                <Input placeholder="Action title" value={form.actionTitle}
                       data-testid="action-title-input"
                       onChange={(e) => setForm({ ...form, actionTitle: e.target.value })} />
                <Input placeholder="Owner email" value={form.actionOwner}
                       data-testid="action-owner-input"
                       onChange={(e) => setForm({ ...form, actionOwner: e.target.value })} />
                <Input placeholder="Due date (YYYY-MM-DD)" value={form.actionDue}
                       data-testid="action-due-input"
                       onChange={(e) => setForm({ ...form, actionDue: e.target.value })} />
              </div>
              <Button size="sm" variant="outline" data-testid="action-add"
                      onClick={() => post(`/problems/${selected.id}/actions`,
                                           { title: form.actionTitle, kind: "preventive",
                                             owner_email: form.actionOwner,
                                             due_date: form.actionDue }, "Action")}>
                Add preventive action
              </Button>
            </div>

            <div className="flex flex-wrap gap-2" data-testid="problem-transitions">
              {(selected.allowed_transitions || []).map((s) => (
                <Button key={s} size="sm" variant="outline" data-testid={`problem-transition-${s}`}
                        onClick={() => post(`/problems/${selected.id}/transition`,
                                             { to_state: s }, `Moved to ${s}`)}>
                  Move to {s}
                </Button>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
